import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getAuthHeaders() {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatExpiry(iso) {
  if (!iso) return 'بدون انتهاء';
  try {
    return new Date(iso).toLocaleDateString('ar-SA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  } catch { return iso; }
}

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    code: '',
    free_days: 30,
    max_uses: '',
    expires_in_days: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchCodes = async () => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/promo-codes`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setCodes(res.data.promo_codes || []);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchCodes(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        free_days: parseInt(form.free_days, 10) || 30,
        description: form.description.trim(),
      };
      if (form.max_uses) payload.max_uses = parseInt(form.max_uses, 10);
      if (form.expires_in_days) payload.expires_in_days = parseInt(form.expires_in_days, 10);
      await axios.post(
        `${BACKEND_URL}/api/admin/promo-codes`,
        payload,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setSuccess(`✅ تم إنشاء الكود ${payload.code} بنجاح`);
      setForm({ code: '', free_days: 30, max_uses: '', expires_in_days: '', description: '' });
      fetchCodes();
    } catch (err) {
      setError(err.response?.data?.detail || 'فشل إنشاء الكود');
    } finally { setSubmitting(false); }
  };

  const handleToggle = async (code) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/promo-codes/${code}/toggle`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      fetchCodes();
    } catch (e) {/* ignore */}
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`حذف الكود "${code}" نهائياً؟`)) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/promo-codes/${code}`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      fetchCodes();
    } catch (e) {/* ignore */}
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
  };

  const inputStyle = {
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '8px',
    color: 'white',
    fontSize: '14px',
    width: '100%'
  };

  const labelStyle = {
    display: 'block',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '12px',
    marginBottom: '6px',
    fontWeight: 600
  };

  return (
    <div data-testid="admin-promo-codes-section">
      <h2 style={{ color: 'white', marginBottom: '24px', fontSize: '22px' }}>
        🎁 أكواد الاشتراك المجاني
      </h2>

      {/* نموذج إنشاء كود */}
      <form
        onSubmit={handleCreate}
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '14px',
          padding: '20px',
          marginBottom: '28px'
        }}
      >
        <h3 style={{ color: 'white', fontSize: '16px', marginBottom: '16px' }}>
          ✨ إنشاء كود جديد
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <div>
            <label style={labelStyle}>الكود *</label>
            <input
              data-testid="admin-promo-code-input"
              type="text"
              value={form.code}
              onChange={(e) => setForm({...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')})}
              placeholder="WIDAD30"
              style={{...inputStyle, fontFamily: 'monospace', letterSpacing: '1.5px'}}
              maxLength={30}
              required
            />
          </div>
          <div>
            <label style={labelStyle}>عدد الأيام المجانية *</label>
            <input
              data-testid="admin-promo-days-input"
              type="number"
              min="1"
              max="3650"
              value={form.free_days}
              onChange={(e) => setForm({...form, free_days: e.target.value})}
              style={inputStyle}
              required
            />
          </div>
          <div>
            <label style={labelStyle}>حد الاستخدامات (اختياري)</label>
            <input
              data-testid="admin-promo-maxuses-input"
              type="number"
              min="1"
              value={form.max_uses}
              onChange={(e) => setForm({...form, max_uses: e.target.value})}
              placeholder="بدون حد"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>ينتهي خلال (يوم) - اختياري</label>
            <input
              data-testid="admin-promo-expires-input"
              type="number"
              min="1"
              value={form.expires_in_days}
              onChange={(e) => setForm({...form, expires_in_days: e.target.value})}
              placeholder="بدون انتهاء"
              style={inputStyle}
            />
          </div>
        </div>
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>وصف داخلي (اختياري)</label>
          <input
            data-testid="admin-promo-desc-input"
            type="text"
            value={form.description}
            onChange={(e) => setForm({...form, description: e.target.value})}
            placeholder="مثلاً: حملة رمضان 2026"
            style={inputStyle}
            maxLength={200}
          />
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.15)', color: '#FCA5A5',
            padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px'
          }}>{error}</div>
        )}
        {success && (
          <div style={{
            background: 'rgba(16, 185, 129, 0.15)', color: '#6EE7B7',
            padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px'
          }}>{success}</div>
        )}

        <button
          data-testid="admin-create-promo-btn"
          type="submit"
          disabled={submitting || !form.code || !form.free_days}
          style={{
            padding: '12px 28px',
            background: 'linear-gradient(135deg, #F59E0B, #D97706)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: 700,
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.6 : 1
          }}
        >
          {submitting ? '⏳ جاري الإنشاء...' : '✨ إنشاء الكود'}
        </button>
      </form>

      {/* قائمة الأكواد */}
      <h3 style={{ color: 'white', fontSize: '16px', marginBottom: '14px' }}>
        📋 الأكواد الحالية ({codes.length})
      </h3>
      {loading ? (
        <div style={{ color: 'rgba(255,255,255,0.6)', padding: '20px' }}>جاري التحميل...</div>
      ) : codes.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '40px',
          color: 'rgba(255,255,255,0.5)',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '12px'
        }}>
          <div style={{ fontSize: '40px', marginBottom: '10px' }}>📭</div>
          <p style={{ margin: 0 }}>لا توجد أكواد بعد. أنشئ أول كود من النموذج أعلاه.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {codes.map((c) => (
            <div
              key={c.code}
              data-testid={`promo-row-${c.code}`}
              style={{
                background: c.is_active ? 'rgba(255,255,255,0.04)' : 'rgba(100,100,100,0.08)',
                border: `1px solid ${c.is_active ? 'rgba(245, 158, 11, 0.3)' : 'rgba(150,150,150,0.2)'}`,
                borderRadius: '12px',
                padding: '14px 18px',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '180px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <code
                    style={{
                      color: c.is_active ? '#FBBF24' : '#9CA3AF',
                      fontSize: '17px',
                      fontWeight: 700,
                      letterSpacing: '1.5px',
                      cursor: 'pointer'
                    }}
                    onClick={() => copyCode(c.code)}
                    title="انقر للنسخ"
                  >
                    {c.code}
                  </code>
                  {!c.is_active && (
                    <span style={{
                      background: 'rgba(150,150,150,0.25)', color: '#9CA3AF',
                      padding: '2px 8px', borderRadius: '99px', fontSize: '10px', fontWeight: 700
                    }}>معطّل</span>
                  )}
                </div>
                {c.description && (
                  <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '12px' }}>
                    {c.description}
                  </div>
                )}
              </div>
              <div style={{
                display: 'flex', gap: '14px', flexWrap: 'wrap',
                fontSize: '12px', color: 'rgba(255,255,255,0.65)'
              }}>
                <span>📅 <strong style={{ color: 'white' }}>{c.free_days}</strong> يوم</span>
                <span>👥 <strong style={{ color: 'white' }}>{c.uses_count || 0}</strong>{c.max_uses ? ` / ${c.max_uses}` : ''}</span>
                <span>⏰ {formatExpiry(c.expires_at)}</span>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  data-testid={`promo-copy-${c.code}`}
                  onClick={() => copyCode(c.code)}
                  style={{
                    padding: '8px 10px', background: 'rgba(99,102,241,0.2)',
                    border: '1px solid rgba(99,102,241,0.4)', borderRadius: '8px',
                    color: '#A5B4FC', cursor: 'pointer', fontSize: '13px'
                  }}
                  title="نسخ"
                >📋</button>
                <button
                  data-testid={`promo-toggle-${c.code}`}
                  onClick={() => handleToggle(c.code)}
                  style={{
                    padding: '8px 10px',
                    background: c.is_active ? 'rgba(150,150,150,0.2)' : 'rgba(16,185,129,0.2)',
                    border: `1px solid ${c.is_active ? 'rgba(150,150,150,0.4)' : 'rgba(16,185,129,0.4)'}`,
                    borderRadius: '8px',
                    color: c.is_active ? '#D1D5DB' : '#6EE7B7',
                    cursor: 'pointer', fontSize: '13px'
                  }}
                  title={c.is_active ? 'تعطيل' : 'تفعيل'}
                >{c.is_active ? '⏸️' : '▶️'}</button>
                <button
                  data-testid={`promo-delete-${c.code}`}
                  onClick={() => handleDelete(c.code)}
                  style={{
                    padding: '8px 10px', background: 'rgba(239,68,68,0.2)',
                    border: '1px solid rgba(239,68,68,0.4)', borderRadius: '8px',
                    color: '#FCA5A5', cursor: 'pointer', fontSize: '13px'
                  }}
                  title="حذف نهائياً"
                >🗑️</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
